import React, { useState, useRef, useEffect } from 'react';
import { supabase_admin } from '../supabase_admin';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/InviteUsersModal.css';

function InviteUsersModal({ isOpen, onClose, selectedWorkspaceId }) {
  const { language } = useLanguage();
  const [selectedRole, setSelectedRole] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [hasSensitiveAccess, setHasSensitiveAccess] = useState(false);
  const [emails, setEmails] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const roles = [
    {
      name: language === 'en' ? 'Admin' : 'Administrator',
      description: language === 'en' 
        ? 'Can access and edit everything within this workspace, including settings and user management.'
        : 'Kann auf alles in diesem Arbeitsbereich zugreifen und es bearbeiten, einschließlich Einstellungen und Benutzerverwaltung.'
    },
    {
      name: language === 'en' ? 'Contributor' : 'Mitwirkender',
      description: language === 'en'
        ? 'Can access and edit content within this workspace. Access to sensitive documents can be enabled individually.'
        : 'Kann auf Inhalte in diesem Arbeitsbereich zugreifen und sie bearbeiten. Der Zugriff auf sensible Dokumente kann individuell aktiviert werden.'
    },
    {
      name: language === 'en' ? 'Reporter' : 'Berichterstatter',
      description: language === 'en'
        ? 'Can create and edit own timesheets and project reports within this workspace.'
        : 'Kann eigene Zeiterfassungen und Projektberichte in diesem Arbeitsbereich erstellen und bearbeiten.'
    }
  ];

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInvite = async () => {
    if (!selectedRole) {
      setError(language === 'en' ? 'Please select a role for the invited users' : 'Bitte wählen Sie eine Rolle für die eingeladenen Benutzer');
      return;
    }

    if (!emails.trim()) {
      setError(language === 'en' ? 'Please enter at least one email address' : 'Bitte geben Sie mindestens eine E-Mail-Adresse ein');
      return;
    }

    setError('');
    setSuccessMessage('');

    const emailList = emails
      .split(/[,;\n]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'));

    if (emailList.length === 0) {
      setError(language === 'en' ? 'No valid email addresses found' : 'Keine gültigen E-Mail-Adressen gefunden');
      return;
    }

    try {
      // Get current user's ID
      const { data: { user: currentUser } } = await supabase_admin.auth.getUser();
      if (!currentUser) {
        throw new Error('Current user not found');
      }

      for (const email of emailList) {
        // Send invitation using Supabase's built-in system
        const { data: inviteData, error: inviteError } = await supabase_admin.auth.admin.inviteUserByEmail(email, {
          redirectTo: `${window.location.origin}/dashboard`,
          data: {
            role: selectedRole,
            has_sensitive_access: hasSensitiveAccess
          }
        });

        if (inviteError) {
          throw inviteError;
        }

        // Map role to system_role
        let systemRole;
        switch (selectedRole) {
          case 'Admin':
            systemRole = 'ws_admin';
            break;
          case 'Contributor':
            systemRole = 'ws_contributor';
            break;
          case 'Reporter':
            systemRole = 'ws_reporter';
            break;
          default:
            throw new Error('Invalid role selected');
        }

        // Create user profile
        const { error: profileError } = await supabase_admin
          .from('user_profiles')
          .insert({
            id: inviteData.user.id,
            system_role: systemRole,
            org_id: '7e8835a5-5c90-487a-82b0-62758d80435c',
            is_deleted: false,
            created_by: currentUser.id,
            is_deactivated: false
          });

        if (profileError) {
          throw profileError;
        }

        // Create user workspace role
        const { error: workspaceRoleError } = await supabase_admin
          .from('user_workspace_roles')
          .insert({
            user_id: inviteData.user.id,
            workspace_id: selectedWorkspaceId,
            created_at: new Date().toISOString(),
            workspace_role: systemRole,
            created_by: currentUser.id,
            has_sensitive_access: hasSensitiveAccess,
            updated_by: currentUser.id
          });

        if (workspaceRoleError) {
          throw workspaceRoleError;
        }
      }

      setSuccessMessage(language === 'en' ? 'Invitations sent successfully!' : 'Einladungen erfolgreich versendet!');
      setEmails('');
      setSelectedRole('');
      setHasSensitiveAccess(false);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(language === 'en' 
        ? `Failed to send invitations: ${err.message}`
        : `Einladungen konnten nicht gesendet werden: ${err.message}`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="invite-users-modal-overlay">
      <div className="invite-users-modal-content">
        <div className="invite-users-modal-header">
          <h2>{language === 'en' ? 'Invite users' : 'Benutzer einladen'}</h2>
          <button className="invite-users-close-button" onClick={onClose}>×</button>
        </div>

        <div className="invite-users-modal-body">
          <div className="invite-users-role-selector">
            <label>{language === 'en' ? 'Invite as' : 'Einladen als'}</label>
            <div className="invite-users-select-wrapper" ref={dropdownRef}>
              <div 
                className="custom-select-header"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                {selectedRole || (language === 'en' ? "Select a role" : "Rolle auswählen")}
              </div>
              {isDropdownOpen && (
                <div className="custom-select-dropdown">
                  {roles.map((role) => (
                    <div
                      key={role.name}
                      className={`custom-select-option ${selectedRole === role.name ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedRole(role.name);
                        setIsDropdownOpen(false);
                        // Force a re-render of the description
                        const description = roles.find(r => r.name === role.name)?.description;
                        if (description) {
                          const descriptionElement = document.querySelector('.invite-users-role-description');
                          if (descriptionElement) {
                            descriptionElement.style.opacity = '0';
                            setTimeout(() => {
                              descriptionElement.style.opacity = '1';
                            }, 50);
                          }
                        }
                      }}
                    >
                      {role.name}
                      {selectedRole === role.name && (
                        <span className="checkmark">✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedRole && (
              <p className="invite-users-role-description">
                {roles.find(r => r.name === selectedRole)?.description}
              </p>
            )}
          </div>

          <div className="invite-users-sensitive-access">
            <label className="invite-users-checkbox-label">
              <input
                type="checkbox"
                checked={hasSensitiveAccess}
                onChange={(e) => setHasSensitiveAccess(e.target.checked)}
              />
              {language === 'en' 
                ? 'Access to sensitive documents & details'
                : 'Zugriff auf sensible Dokumente & Details'}
            </label>
          </div>

          <div className="invite-users-email-input">
            <label>{language === 'en' ? 'Add email addresses' : 'E-Mail-Adressen hinzufügen'}</label>
            <textarea
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder={language === 'en'
                ? 'Enter email addresses to invite, separated by commas, semicolons, or new lines. For example: "another@example.com, Firstname Lastname <email@example.com>"'
                : 'Geben Sie E-Mail-Adressen ein, die eingeladen werden sollen, getrennt durch Kommas, Semikolons oder neue Zeilen. Zum Beispiel: "another@example.com, Vorname Nachname <email@example.com>"'}
              rows={4}
            />
            {error && <p className="invite-users-error-message">{error}</p>}
            {successMessage && <p className="invite-users-success-message">{successMessage}</p>}
            {emails.trim() && (
              <button className="invite-users-submit-button" onClick={handleInvite}>
                {language === 'en' ? 'Invite users' : 'Benutzer einladen'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InviteUsersModal; 